import React from "react";
import { methodCodeTitle, methodPillLabel, METHOD_TYPE } from "./loginVerifyHelpers";
import "./loginVerifyFlow.css";

/**
 * Screen 2 — enter OTP / authenticator code for the active method.
 */
export default function LoginMethodCodePanel({
  methods = [],
  selectedType,
  completedTypes = [],
  remainingTypes = [],
  verificationMode = "",
  title,
  description,
  otpDigitsDisplay = [],
  otpInputRefs,
  resendTimer = 0,
  verifyBusy = false,
  onSubmit,
  onResend,
  onPaste,
  onCellChange,
  onCellKeyDown,
  onRowPaste,
  onBackToMethods,
  onSwitchMethod,
  showPasskeyPanel = false,
  passkeyBusy = false,
  onPasskeyAuth,
  onClosePasskey,
  onUnableToVerify,
  recoveryBusy = false,
  isRecovery = false,
  backLabel,
}) {
  const active = Number(selectedType);
  const heading = title || methodCodeTitle(active);

  if (showPasskeyPanel) {
    return (
      <div className="lmv-panel lmv-panel--code">
        <div className="passkey-modal-overlay passkey-modal-overlay--loginPasskey" role="presentation">
          <div
            className="passkey-modal passkey-modal--loginPasskey"
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-passkey-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="passkey-modal__close" onClick={onClosePasskey} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
            <div className="passkey-modal__fingerprintRing" aria-hidden="true">
              <img src="/images/passkey_authentication_vector.svg" alt="passkey" />
            </div>
            <h2 id="login-passkey-title" className="passkey-modal__title">
              Passkey Authentication
            </h2>
            <p className="passkey-modal__subtitle">Click the button below to authenticate with your passkey.</p>
            <p className="passkey-modal__hintLine">Use your registered passkey to verify</p>
            <p className="passkey-modal__hintLine passkey-modal__hintLine--muted">
              This will prompt Face ID, Touch ID, or Windows Hello
            </p>
            <button
              type="button"
              className="passkey-modal__loginPrimaryBtn"
              onClick={onPasskeyAuth}
              disabled={passkeyBusy}
            >
              {passkeyBusy ? "Authenticating..." : "Authenticate with Passkey"}
            </button>
            <button type="button" className="passkey-verify-modal__chooseOther passkey-modal__loginSwitchLink" onClick={onBackToMethods}>
              Back to methods <i className="ri-external-link-line" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      className="lmv-panel lmv-panel--code profile_form"
      onSubmit={(e) => {
        e.preventDefault();
        if (verifyBusy) return;
        onSubmit?.();
      }}
      noValidate
    >
      <h1 className="login-wizard-title login-wizard-title--verify">{heading}</h1>
      {description ? <p className="signup-wizard-subtitle">{description}</p> : null}

      {methods.length > 1 ? (
        <ul className="login-wizard-method-progress" aria-label="Verification progress">
          {methods.map((m) => {
            const t = Number(m.type);
            const done = completedTypes.includes(t);
            const remaining = remainingTypes.length > 0 ? remainingTypes.includes(t) : !done;
            const isActive = t === active;
            return (
              <li
                key={t}
                className={done ? "is-done" : isActive ? "is-active" : remaining ? "is-pending" : ""}
              >
                <button
                  type="button"
                  className="lmv-pill-btn"
                  disabled={done && verificationMode === "ALL_REQUIRED"}
                  onClick={() => {
                    if (done && verificationMode === "ALL_REQUIRED") return;
                    onSwitchMethod?.(m);
                  }}
                >
                  {methodPillLabel(m, { active: isActive, done })}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <div
        className="signup-wizard-otp-stack otp_stack_login"
        onPaste={onRowPaste}
        role="group"
        aria-label="6-digit verification code"
      >
        <div className="signup-wizard-otp-row">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <input
              key={i}
              id={i === 0 ? "login-otp-cell-0" : undefined}
              ref={(el) => {
                if (otpInputRefs?.current) otpInputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? "one-time-code" : "off"}
              name={i === 0 ? "one-time-code" : undefined}
              className="signup-wizard-otp-cell"
              maxLength={1}
              value={otpDigitsDisplay[i] || ""}
              onChange={(e) => onCellChange?.(i, e)}
              onKeyDown={(e) => onCellKeyDown?.(i, e)}
              onPaste={onRowPaste}
              aria-label={`Digit ${i + 1} of 6`}
            />
          ))}
        </div>
        <div
          className={`signup-wizard-otp-actions${active === METHOD_TYPE.AUTHENTICATOR ? " login-otp-actions-paste-only" : ""}`}
        >
          {active !== METHOD_TYPE.AUTHENTICATOR ? (
            <button type="button" className="signup-wizard-otp-link" disabled={resendTimer > 0} onClick={onResend}>
              {resendTimer > 0 ? `Resend (${resendTimer}s)` : "Resend"}
            </button>
          ) : (
            <span className="lmv-otp-actions-spacer" aria-hidden="true" />
          )}
          <button type="button" className="signup-wizard-otp-link signup-wizard-otp-paste" onClick={onPaste}>
            Paste
            <i className="ri-file-copy-line" aria-hidden />
          </button>
        </div>
      </div>

      <div className="login_btn lmv-code-submit">
        <button
          type="submit"
          className="login-wizard-next-btn"
          disabled={verifyBusy || String(otpDigitsDisplay.join("")).replace(/\D/g, "").length < 6}
          aria-busy={verifyBusy}
        >
          {verifyBusy ? (
            <>
              <span className="login-wizard-next-btn__spinner" aria-hidden />
              <span>Verifying…</span>
            </>
          ) : (
            "Next"
          )}
        </button>
      </div>

      {!isRecovery && typeof onUnableToVerify === "function" ? (
        <button type="button" className="lmv-cant-access" disabled={recoveryBusy} onClick={onUnableToVerify}>
          Unable to verify?
        </button>
      ) : null}

      <button type="button" className="signup-wizard-back" onClick={onBackToMethods}>
        {backLabel || (isRecovery ? "← Cancel recovery" : "← Back to methods")}
      </button>
    </form>
  );
}
